<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import CaptainTestRuns from 'dashboard/api/captain/testRuns';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const assistantId = computed(() => Number(route.params.assistantId));
const runs = ref([]);
const selectedRun = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);
const activeFilter = ref(0);

const filters = computed(() => [
  { label: t('CAPTAIN.TESTS.FILTERS.ALL') },
  { label: t('CAPTAIN.TESTS.FILTERS.UNRESOLVED') },
  { label: t('CAPTAIN.TESTS.FILTERS.POOR') },
]);

const filteredRuns = computed(() => {
  if (activeFilter.value === 1) {
    return runs.value.filter(run => run.status === 'unresolved');
  }
  if (activeFilter.value === 2) {
    return runs.value.filter(run => run.rating === 'poor');
  }
  return runs.value;
});

const ratingLabel = rating =>
  ({
    good: t('CAPTAIN.TESTS.RATINGS.GOOD'),
    acceptable: t('CAPTAIN.TESTS.RATINGS.ACCEPTABLE'),
    poor: t('CAPTAIN.TESTS.RATINGS.POOR'),
  })[rating];

const loadRuns = async () => {
  isLoading.value = true;
  try {
    const { data } = await CaptainTestRuns.get({
      assistantId: assistantId.value,
    });
    runs.value = data.payload || [];
    selectedRun.value = runs.value[0] || null;
  } finally {
    isLoading.value = false;
  }
};

const handleFilterChange = filter => {
  activeFilter.value = filters.value.findIndex(
    item => item.label === filter.label
  );
  selectedRun.value = filteredRuns.value[0] || null;
};

const updateRun = async attributes => {
  if (!selectedRun.value) return;
  isSaving.value = true;
  try {
    const { data } = await CaptainTestRuns.update(
      selectedRun.value.id,
      attributes
    );
    const index = runs.value.findIndex(run => run.id === data.id);
    if (index !== -1) runs.value[index] = data;
    selectedRun.value = data;
  } catch (error) {
    useAlert(t('CAPTAIN.TESTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openPlayground = () => {
  router.push({
    name: 'captain_assistants_playground_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};

watch(assistantId, loadRuns);
onMounted(loadRuns);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.TESTS.HEADER')"
    :show-pagination-footer="false"
    :show-know-more="false"
    class="h-full"
  >
    <template #search>
      <Button
        :label="$t('CAPTAIN.TESTS.RUN_TEST')"
        icon="i-lucide-flask-conical"
        size="sm"
        @click="openPlayground"
      />
    </template>

    <template #subHeader>
      <TabBar
        :tabs="filters"
        :initial-active-tab="activeFilter"
        @tab-changed="handleFilterChange"
      />
    </template>

    <template #body>
      <div v-if="isLoading" class="flex h-full items-center justify-center">
        <Spinner />
      </div>

      <div
        v-else
        class="grid h-full min-h-0 grid-cols-1 overflow-hidden rounded-lg outline outline-1 -outline-offset-1 outline-n-weak"
        :class="{
          'lg:grid-cols-[minmax(22rem,0.9fr)_minmax(26rem,1.1fr)]':
            filteredRuns.length,
        }"
      >
        <section
          class="min-h-0 overflow-y-auto"
          :class="{ 'border-r border-n-weak': filteredRuns.length }"
        >
          <header class="border-b border-n-weak px-4 py-3">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{
                $t('CAPTAIN.TESTS.QUESTION_COUNT', {
                  count: filteredRuns.length,
                })
              }}
            </p>
          </header>
          <button
            v-for="run in filteredRuns"
            :key="run.id"
            type="button"
            class="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-n-weak px-4 py-4 text-left hover:bg-n-alpha-1"
            :class="{ 'bg-n-alpha-2': selectedRun?.id === run.id }"
            @click="selectedRun = run"
          >
            <span class="min-w-0">
              <span class="block truncate text-sm font-medium text-n-slate-12">
                {{ run.question }}
              </span>
              <span class="mt-1 block truncate text-xs text-n-slate-10">
                {{ run.answer || $t('CAPTAIN.TESTS.NO_ANSWER') }}
              </span>
            </span>
            <span
              class="rounded-md px-2 py-0.5 text-xs capitalize outline outline-1 -outline-offset-1"
              :class="
                run.rating === 'poor' || run.status === 'unresolved'
                  ? 'bg-n-ruby-3 text-n-ruby-11 outline-n-ruby-5'
                  : run.rating === 'good'
                    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
                    : 'bg-n-slate-2 text-n-slate-10 outline-n-weak'
              "
            >
              {{ run.rating || run.status }}
            </span>
          </button>
          <div
            v-if="!filteredRuns.length"
            class="flex min-h-96 flex-col items-center justify-center px-6 text-center"
          >
            <span
              class="mb-4 flex size-11 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-flask-conical size-5" />
            </span>
            <h2 class="mb-1 text-base font-semibold text-n-slate-12">
              {{
                runs.length
                  ? $t('CAPTAIN.TESTS.EMPTY')
                  : $t('CAPTAIN.TESTS.EMPTY_TITLE')
              }}
            </h2>
            <p
              v-if="!runs.length"
              class="mb-4 max-w-md text-sm text-n-slate-10"
            >
              {{ $t('CAPTAIN.TESTS.EMPTY_DESCRIPTION') }}
            </p>
            <Button
              v-if="!runs.length"
              :label="$t('CAPTAIN.TESTS.RUN_FIRST_TEST')"
              icon="i-lucide-play"
              size="sm"
              @click="openPlayground"
            />
          </div>
        </section>

        <aside v-if="selectedRun" class="min-h-0 overflow-y-auto px-5 py-5">
          <div class="mb-5 flex items-start justify-between gap-3">
            <div>
              <p class="mb-1 text-xs font-medium uppercase text-n-slate-9">
                {{ $t('CAPTAIN.TESTS.EVALUATE') }}
              </p>
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                {{ selectedRun.question }}
              </h2>
            </div>
            <Button
              v-if="selectedRun.status === 'unresolved'"
              :label="$t('CAPTAIN.TESTS.MARK_RESOLVED')"
              color="slate"
              size="sm"
              @click="updateRun({ status: 'answered' })"
            />
          </div>

          <div
            class="rounded-lg bg-n-alpha-1 px-4 py-4 text-sm text-n-slate-12"
          >
            {{ selectedRun.answer || $t('CAPTAIN.TESTS.NO_ANSWER') }}
          </div>

          <section v-if="selectedRun.sources?.length" class="mt-5">
            <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
              {{ $t('CAPTAIN.TESTS.SOURCES') }}
            </h3>
            <a
              v-for="source in selectedRun.sources"
              :key="source.url"
              :href="source.url"
              target="_blank"
              rel="noopener noreferrer"
              class="mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-n-blue-11 outline outline-1 -outline-offset-1 outline-n-weak hover:bg-n-alpha-1"
            >
              <span class="i-lucide-book-open size-4" />
              <span class="truncate">{{ source.title || source.url }}</span>
            </a>
          </section>

          <section class="mt-6 border-t border-n-weak pt-5">
            <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
              {{ $t('CAPTAIN.TESTS.RATE') }}
            </h3>
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="rating in ['good', 'acceptable', 'poor']"
                :key="rating"
                type="button"
                class="rounded-md px-3 py-2 text-sm outline outline-1 -outline-offset-1"
                :class="
                  selectedRun.rating === rating
                    ? 'bg-n-teal-3 text-n-teal-11 outline-n-teal-5'
                    : 'bg-n-solid-1 text-n-slate-11 outline-n-weak hover:bg-n-alpha-1'
                "
                @click="updateRun({ rating })"
              >
                {{ ratingLabel(rating) }}
              </button>
            </div>
          </section>

          <section class="mt-6 border-t border-n-weak pt-5">
            <TextArea
              v-model="selectedRun.note"
              :label="$t('CAPTAIN.TESTS.NOTE')"
              :placeholder="$t('CAPTAIN.TESTS.NOTE_PLACEHOLDER')"
              auto-height
              resize
            />
            <div class="mt-3 flex justify-end">
              <Button
                :label="$t('CAPTAIN.TESTS.SAVE_NOTE')"
                :is-loading="isSaving"
                size="sm"
                @click="updateRun({ note: selectedRun.note })"
              />
            </div>
          </section>
        </aside>
      </div>
    </template>
  </PageLayout>
</template>
